import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Paper,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

function RecipeSearchDevOps({ token, lang }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  // ===================== SEARCH =====================
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }

    const delay = setTimeout(() => {
      search();
    }, 300);

    return () => clearTimeout(delay);
  }, [query]);

  const search = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/RecipePreparation/SearchRecipes?query=${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      setResults(data || []);

      // ✅ open seulement si résultats
      setOpen((data || []).length > 0);
    } catch (err) {
      console.error(err);
      setResults([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // ===================== CLICK OUTSIDE =====================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ===================== NAVIGATION =====================
  const goToRecipe = (id) => {
    navigate(`/Recipe/${id}`);
    setQuery("");
    setOpen(false);
  };

  const label = (r) => (lang === "FR" ? r.nomFr : r.nomEn);

  return (
    <Box
      ref={wrapperRef}
      sx={{ position: "relative", width: 350 }}
    >
      {/* ================= INPUT ================= */}
      <TextField
        fullWidth
        size="small"
        placeholder={lang === "FR" ? "Rechercher une recette..." : "Search recipes..."}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: loading && (
            <CircularProgress size={16} />
          ),
        }}
        sx={{
          background: "#f8f9fb",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          width: "100%",
          "& .MuiInputBase-root": {
                height: 40, // 👈 même taille partout
              },
        }}
      />

      {/* ================= DROPDOWN ================= */}
      {open && (
        <Paper
          sx={{
            position: "absolute",
            top: "110%",
            left: 0,
            right: 0,
            zIndex: 2000,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
            boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
          }}
        >
          {/* LOADING */}
          {loading && (
            <Box p={2}>
              <Typography fontSize={13}>Searching...</Typography>
            </Box>
          )}

          {/* EMPTY */}
          {!loading && results.length === 0 && (
            <Box p={2}>
              <Typography fontSize={13} color="text.secondary">
                No recipes found
              </Typography>
            </Box>
          )}

          {/* RESULTS */}
          {!loading &&
            results.map((r) => (
              <Box
                key={r.id}
                onClick={() => goToRecipe(r.id)}
                sx={{
                  p: 1.5,
                  cursor: "pointer",
                  "&:hover": { background: "#f5f5f5" },
                }}
              >
                <Typography fontSize={14} fontWeight={600}>
                  {label(r)}
                </Typography>

                <Typography fontSize={11} color="text.secondary">
                  Recipe #{r.id}
                </Typography>
              </Box>
            ))}
        </Paper>
      )}
    </Box>
  );
}

export default RecipeSearchDevOps;